import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { XIcon, SealCheckIcon, TrophyIcon } from "@phosphor-icons/react";

/**
 * RecentApprovalsBanner — a sticky social-proof strip pinned to the very top
 * of the Landing page. Cycles through the most recently ID-verified users
 * (and flags Founder Sellers with a trophy pill). Polls every 20s so it
 * feels alive without WebSocket complexity.
 *
 * Privacy: only the public @handle is shown — no city, no full name, no
 * email. (Per user spec: "@username + just verified only".)
 *
 * Behavior:
 *   • Polls /api/stats/recently-verified every 20s.
 *   • Cycles to the next item every ~3.5s with a soft slide-up animation.
 *   • Dismissible — sessionStorage keeps it dismissed for the session so we
 *     don't re-pester the same visitor on every navigation.
 *   • Renders nothing if there are no approvals or the user dismissed it.
 */
export default function RecentApprovalsBanner() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const pollRef = useRef(null);
  const cycleRef = useRef(null);

  // Honour prior dismissal for the session.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("bidorah_approvals_banner_dismissed") === "1") {
        setDismissed(true);
      }
    } catch (_) {
      /* ignore */
    }
  }, []);

  // Initial load + 20s polling.
  useEffect(() => {
    if (dismissed) return;
    // Belt-and-suspenders: even if the server somehow leaks a non-handle
    // value (email, name with a space, etc.), we *never* render it. Only
    // strings matching the canonical @handle pattern are allowed through.
    const HANDLE_RE = /^[a-z][a-z0-9_]{2,19}$/;
    const fetchApprovals = async () => {
      try {
        const r = await api.get("/stats/recently-verified", {
          params: { limit: 8 },
        });
        const fresh = (r.data?.items || []).filter(
          (x) => x.username && HANDLE_RE.test(String(x.username).toLowerCase())
        );
        if (fresh.length) {
          setItems((prev) => {
            // Detect a brand-new approval at the top of the list — when that
            // happens, jump the cycle to it so the new face is celebrated.
            const newestUserId = fresh[0]?.user_id;
            const wasNewest = prev[0]?.user_id;
            if (newestUserId && newestUserId !== wasNewest) {
              setIdx(0);
              setAnimKey((k) => k + 1);
            }
            return fresh;
          });
        }
      } catch (_) {
        /* network — silently retry on next tick */
      }
    };
    fetchApprovals();
    pollRef.current = setInterval(fetchApprovals, 20000);
    return () => clearInterval(pollRef.current);
  }, [dismissed]);

  // Cycle through items every ~3.5s.
  useEffect(() => {
    if (dismissed || items.length <= 1) return;
    cycleRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
      setAnimKey((k) => k + 1);
    }, 3500);
    return () => clearInterval(cycleRef.current);
  }, [items.length, dismissed]);

  const onDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem("bidorah_approvals_banner_dismissed", "1");
    } catch (_) {
      /* ignore */
    }
  };

  if (dismissed || !items.length) return null;
  const cur = items[idx % items.length];
  if (!cur) return null;

  const isFounderSeller = cur.is_founder && cur.role === "seller";

  return (
    <div
      className="relative bg-gradient-to-r from-emerald-50 via-emerald-100/70 to-emerald-50 border-b border-emerald-200/70"
      data-testid="recent-approvals-banner"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 h-9 relative flex items-center justify-center text-xs">
        {/* Centered content cluster: pulsing dot + label + cycling line. */}
        <div className="flex items-center gap-3 min-w-0 max-w-full">
          {/* Pulsing dot — the universal "live" affordance. */}
          <span className="relative inline-flex flex-shrink-0" aria-hidden="true">
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
            <span className="relative h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-emerald-100" />
          </span>

          <span className="font-mono tracking-[0.18em] uppercase text-[10px] font-bold text-emerald-700 hidden sm:inline">
            Live · Just approved
          </span>

          {/* Cycling line — keyed on animKey so each transition restarts the
              slide-up animation cleanly. */}
          <div className="min-w-0 overflow-hidden">
            <div
              key={animKey}
              className="flex items-center gap-2 truncate animate-[slideUpFade_0.45s_ease-out]"
            >
              {isFounderSeller ? (
                <TrophyIcon size={12} weight="fill" className="text-amber-500 flex-shrink-0" />
              ) : (
                <SealCheckIcon size={12} weight="fill" className="text-emerald-600 flex-shrink-0" />
              )}
              <span className="text-zinc-700 truncate">
                <span className="font-bold text-zinc-950">@{cur.username}</span>
                {isFounderSeller ? (
                  <>
                    {" "}
                    <span className="text-amber-700 font-semibold">
                      just approved as Founder Seller
                    </span>
                  </>
                ) : (
                  <>
                    {" "}
                    <span className="text-emerald-700 font-semibold">just verified</span>
                  </>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Dismiss anchored to the right edge so it doesn't pull the
            centered content off-axis. */}
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center rounded-full hover:bg-emerald-200/60 text-emerald-700/80 hover:text-emerald-900 transition-colors"
          aria-label="Dismiss"
          data-testid="recent-approvals-banner-dismiss"
        >
          <XIcon size={12} weight="bold" />
        </button>
      </div>

      {/* Local keyframes — kept inline so the component is fully self-
          contained and doesn't require touching tailwind.config or global
          CSS for a one-off slide-up animation. */}
      <style>{`
        @keyframes slideUpFade {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
